/* Tri-Art acrylic mediums: live-rendered swatch cards and product-page specs.
   Cards:    [data-ms-card]   (snippets/medium-swatch.liquid)
   Toggle:   [data-ms-toggle] (snippets/medium-wet-dry-toggle.liquid)
   Section:  [data-ms-specs]  (sections/medium-specs.liquid)
   Rendering runs in a Web Worker with OffscreenCanvas where the browser supports it, so scrolling stays smooth on phones.
   Older browsers fall back to rendering on the main thread in small slices. */
(function () {
  'use strict';
  if (window.TriArtMediums) return;

/* Tri-Art medium swatch renderer: core.
   Self-contained so it can run on the main thread or inside a Web Worker (its source is stringified).
   p = {h, gf:[min,max]|null, hex, tpl, form, k, kw, shrink, part, haze, wetTint, tooth, demo, uv}
   build(p,R) -> state-independent buffers. shade(buf,t,Lx,Ly,uvAmt,img) -> pixels.
   t: 0 wet .. 1 dry. Lx,Ly: light position in card units. uvAmt: 0 studio .. 1 black light. */
function MS_CORE(){
  const clamp=(x,a,b)=>x<a?a:x>b?b:x, lerp=(a,b,t)=>a+(b-a)*t;
  const sstep=(a,b,x)=>{const t=clamp((x-a)/(b-a),0,1);return t*t*(3-2*t)};
  const hex2=h=>[parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)];
  function hash(s){let h=2166136261;for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619)}return h>>>0}
  function rng(seed){let a=seed>>>0;return()=>{a=a+0x6D2B79F5|0;let t=Math.imul(a^a>>>15,1|a);t=t+Math.imul(t^t>>>7,61|t)^t;return((t^t>>>14)>>>0)/4294967296}}

  /* tileable fbm noise, generated once */
  const NS=256;
  function makeFbm(seed,base,oct,persist){
    const r=rng(seed),out=new Float32Array(NS*NS);let amp=1;
    for(let o=0;o<oct;o++){const P=base<<o,lat=new Float32Array(P*P);for(let i=0;i<P*P;i++)lat[i]=r();const sc=P/NS;
      for(let y=0;y<NS;y++){const fy=y*sc,y0=fy|0,ty=fy-y0,sy=ty*ty*(3-2*ty),y1=(y0+1)%P;
        for(let x=0;x<NS;x++){const fx=x*sc,x0=fx|0,tx=fx-x0,sx=tx*tx*(3-2*tx),x1=(x0+1)%P;
          const a=lat[y0*P+x0],b=lat[y0*P+x1],c=lat[y1*P+x0],d=lat[y1*P+x1],top=a+(b-a)*sx;
          out[y*NS+x]+=amp*(top+((c+(d-c)*sx)-top)*sy)}}amp*=persist}
    let mn=1e9,mx=-1e9;for(let i=0;i<out.length;i++){const v=out[i];if(v<mn)mn=v;if(v>mx)mx=v}
    for(let i=0;i<out.length;i++)out[i]=(out[i]-mn)/(mx-mn);return out}
  const N1=makeFbm(11,4,5,.5),N2=makeFbm(23,4,5,.55),N3=makeFbm(37,16,3,.6);
  function ns(F,x,y){x=(x%1+1)%1*NS;y=(y%1+1)%1*NS;const x0=x|0,y0=y|0,tx=x-x0,ty=y-y0,x1=(x0+1)&255,y1=(y0+1)&255;
    const a=F[y0*NS+x0],b=F[y0*NS+x1],c=F[y1*NS+x0],d=F[y1*NS+x1],t=a+(b-a)*tx;return t+((c+(d-c)*tx)-t)*ty}

  /* gloss factor -> highlight. Knots tuned so GF 80+ reads properly glossy and GF 14-17 has a visible soft sheen. */
  const GK=[[0,.015,3],[3,.02,3],[8,.045,4.5],[15,.24,12],[25,.33,22],[37,.45,38],[55,.75,110],[75,1.15,300],[85,1.45,440],[92,1.6,540]];
  function specFor(gf){let i=0;while(i<GK.length-2&&gf>GK[i+1][0])i++;const a=GK[i],b=GK[i+1],t=clamp((gf-a[0])/(b[0]-a[0]),0,1);
    return{amp:lerp(a[1],b[1],t),sh:lerp(a[2],b[2],t),halo:Math.max(0,(gf-40)/50)*.15}}
  function parseGF(s){if(!s)return null;if(Array.isArray(s))return s;const m=String(s).match(/[\d.]+/g);if(!m)return null;
    const a=parseFloat(m[0]),b=m[1]?parseFloat(m[1]):a;return[a,b]}

  const HS=.05,CANVAS=[229,224,214],BAND=[20,20,22];
  const HV={yellow:'#fdd629',magenta:'#c1254b',blue:'#0052a1',black:'#2d3134'};

  function sdPatch(u,v,x0,x1,y0,y1,rad,rag,ox,oy){
    const cx=(x0+x1)/2,cy=(y0+y1)/2,hx=(x1-x0)/2-rad,hy=(y1-y0)/2-rad;
    const qx=Math.abs(u-cx)-hx,qy=Math.abs(v-cy)-hy;
    const d=Math.hypot(Math.max(qx,0),Math.max(qy,0))+Math.min(Math.max(qx,qy),0)-rad;
    return d+rag*(ns(N1,u*1.4+ox,v*1.4+oy)-.5)+rag*.35*(ns(N1,u*3.1+oy,v*3.1+ox)-.5)}
  function makeStrokes(rnd,count){
    const s=[];for(let i=0;i<count;i++)s.push({v0:.17+(i+.5)/count*.66+(rnd()-.5)*.05,w:.16+rnd()*.08,a:(rnd()-.5)*.18,
      s:.07+rnd()*.07,e:.85+rnd()*.08,dir:rnd()<.5?1:-1,lip:.28+rnd()*.3,k:rnd()*10});return s}
  function knife(u,v,strokes,ox,oy){
    let h=.3+.2*ns(N1,u*3+ox,v*3+oy);
    for(const s of strokes){
      const q=((v-s.v0)-s.a*(u-.5))/s.w;if(q<-1||q>1)continue;
      const along=s.dir>0?(u-s.s)/(s.e-s.s):(s.e-u)/(s.e-s.s);
      const er=.05*(ns(N1,u*1.5+s.k,v*3+s.k)-.5);
      if(along<er||along>1+er)continue;
      const qq=q*s.dir;
      let p=.55+.22*qq+s.lip*Math.exp(-(((qq-.88)/.085)**2))+.3*s.lip*Math.exp(-(((qq+.9)/.09)**2));
      p+=.22*(ns(N2,u*.55+ox+s.k,q*.6+s.k)-.5);
      p+=.45*sstep(.72,1,along)*(1-Math.abs(2*ns(N1,u*4+s.k,v*4+oy)-1));
      p*=sstep(1,.55,Math.abs(q))*sstep(-.02,.14,along)*sstep(1+er,.955+er,along);
      h=Math.max(h,p)+.1*Math.min(h,p)*sstep(1,.55,Math.abs(q))}
    return h}

  const PARTS={
    nephF:{r:.0055,cov:.42,shape:'facet',hgt:.75,cols:[[244,244,240]],a:[.35,.6],speck:.006},
    nephC:{r:.010,cov:.48,shape:'facet',hgt:.8,cols:[[244,244,240],[230,230,226]],a:[.45,.7],speck:.012},
    nephX:{r:.019,cov:.55,shape:'facet',hgt:.85,cols:[[244,244,240],[222,222,218]],a:[.5,.8],speck:.025},
    copper:{r:.012,cov:.75,shape:'facet',hgt:.9,cols:[[26,25,26],[38,36,37],[48,40,36]],a:[.92,.98],speck:0},
    glass:{r:.013,cov:.62,shape:'facet',hgt:.8,cols:[[196,220,170],[226,220,150],[210,176,112],[232,240,234],[180,214,198]],a:[.35,.62],speck:0},
    walnut:{r:.011,cov:1.25,shape:'round',hgt:.8,cols:[[92,48,15],[124,70,24],[160,100,40],[110,60,20]],a:[1,1],speck:0},
    pete:{r:.03,cov:1.1,shape:'flake',hgt:.12,cols:[[230,238,248],[220,232,246],[236,242,240]],a:[.03,.07],speck:0}
  };
  const bare=g=>({k:0,kw:0,col:[0,0,0],colW:[0,0,0],shrink:1,g,gw:g});

  let CR=new Float32Array(1),DM=new Uint8Array(1);
  function build(p,R){
    const N=R*R,rnd=rng(hash(p.h||'x')),ox=rnd(),oy=rnd();
    if(CR.length<N)CR=new Float32Array(N);else CR.fill(0,0,N);
    if(DM.length<N)DM=new Uint8Array(N);DM.fill(255,0,N);
    const base=new Uint8ClampedArray(N*3),H=new Float32Array(N),P=new Float32Array(N),cls=new Uint8Array(N);
    const gfv=p.gf?(p.gf[0]+p.gf[1])/2:72;
    const film={k:p.k,kw:p.kw,col:p.haze?hex2(p.haze):[247,248,248],colW:p.wetTint?hex2(p.wetTint):[244,244,240],shrink:p.shrink||1.3,g:gfv,gw:92,uv:!!p.uv};
    if(p.hex){const c=hex2(p.hex);film.col=c;film.colW=c.map(x=>x*.86)}
    const classes=[bare(2),film];
    const strokes=makeStrokes(rnd,2+(rnd()*2|0)),W=46,tpl=p.tpl,isVarnish=tpl==='varnish';
    const stripeCols=[HV.yellow,HV.magenta,HV.blue,HV.black].map(hex2);
    const demo=p.demo?(Array.isArray(p.demo)?p.demo.map(hex2):[hex2(p.demo)]):[hex2(HV.blue),hex2(HV.yellow)];
    if(isVarnish){classes[1]=bare(70);classes[2]=film}
    if(tpl==='opacify'){classes[1]=bare(62);const c=demo[0];classes[2]={...film,k:14,kw:1.1,col:c.map(x=>lerp(x,255,.22)),colW:c.map(x=>lerp(x,255,.55)),g:7.5}}
    if(tpl==='retard'||tpl==='screen'){classes[1]={...bare(tpl==='screen'?30:60),gw:90,shrink:p.shrink||1.3}}
    const cells=[];if(tpl==='crackle'){const G=15;for(let j=0;j<G;j++)for(let i=0;i<G;i++)if(rnd()>.18)cells.push([(i+.05+rnd()*.9)/G,(j+.05+rnd()*.9)/G])}
    const h0={level:.1,pool:.32,brush:.15,soft:.3,knife:.42,peaks:.5}[p.form]||.15;
    const inv=1/R;
    for(let y=0,i=0;y<R;y++){const v=(y+.5)*inv;for(let x=0;x<R;x++,i++){const u=(x+.5)*inv;
      const wv=Math.sin(2*Math.PI*u*W)*Math.sin(2*Math.PI*v*W);
      const tone=.98+.012*wv+.05*(ns(N3,u*2+ox,v*2+oy)-.5)+.03*(ns(N3,u*11+oy,v*11+ox)-.5);
      let r=CANVAS[0]*tone,gg=CANVAS[1]*tone,b=CANVAS[2]*tone;
      P[i]=.00014*wv;
      let hh=0,c=0;
      if(!isVarnish&&tpl!=='retard'&&tpl!=='screen'){
        const bd=Math.max(Math.abs(v-.5)-.07,Math.abs(u-.5)-.465)+.004*(ns(N1,u*2.5,v*2.5+ox)-.5);
        const cov=sstep(1.2*inv,-1.2*inv,bd);
        if(cov>0){const k=.88+.24*ns(N3,u*5+oy,v*5);r=lerp(r,BAND[0]*k,cov);gg=lerp(gg,BAND[1]*k,cov);b=lerp(b,BAND[2]*k,cov)}
      }
      if(isVarnish||tpl==='retard'){
        const d=sdPatch(u,v,.07,.93,.08,.92,.04,.03,ox,oy);
        if(d<0){
          let col;
          if(isVarnish){col=stripeCols[clamp(Math.floor((u-.07)/.86*4),0,3)]}
          else{const a=demo[0],bb=demo[1]||demo[0],right=u>.5,wob=.04*(ns(N1,u*3+ox,v*2+oy)-.5);
            const m=right?sstep(.22,.78,v+wob*2):(v+wob>.5?1:0);
            col=[lerp(a[0],bb[0],m),lerp(a[1],bb[1],m),lerp(a[2],bb[2],m)];
            if(right&&m>0&&m<1){const s=1-.18*Math.sin(Math.PI*m);col=col.map(x=>x*s)}}
          const str=ns(N2,u*(isVarnish?3.2:.9)+ox,v*(isVarnish?.6:5)+oy);
          const tk=isVarnish?.10:(p.form==='knife'?.28:.12);
          r=col[0]*(.94+.1*str);gg=col[1]*(.94+.1*str);b=col[2]*(.94+.1*str);
          hh=tk*(.55+.9*str)*sstep(0,.012,-d);c=1;
          if(tpl==='retard'&&p.form==='knife')hh=.36*knife(u,v,strokes,ox,oy)*sstep(0,.03,-d);
          if(isVarnish){
            const vd=v-(.5+.03*(ns(N1,u*2+oy,ox)-.5));
            if(vd>0){c=2;H[i]=.1*sstep(0,.02,vd)*(1+.35*Math.exp(-vd/.02))*sstep(0,.01,-d)}
            P[i]+=hh*HS*.6;
            base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;cls[i]=c;continue;
          }
        }
      }
      if(isVarnish){base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;cls[i]=0;continue}
      if(tpl==='screen'){
        const d=sdPatch(u,v,.1,.9,.12,.88,.01,.004,ox,oy);
        if(d<0){const cs=1/26,cx=(Math.floor(u/cs)+.5)*cs,cy=(Math.floor(v/cs)+.5)*cs;
          const ramp=v<.42?1:clamp(1.15-(u-.1)/.8,0,1),rad=cs*.5*Math.sqrt(ramp)*1.12;
          if(v<.42||Math.hypot(u-cx,v-cy)<rad){const m=demo[0].map(x=>x/255),mesh=.96+.04*Math.sin(u*R*1.3)*Math.sin(v*R*1.3);
            r*=m[0]*mesh;gg*=m[1]*mesh;b*=m[2]*mesh;hh=.06;c=1}}
        base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;H[i]=hh;cls[i]=c;continue;
      }
      if(tpl==='retard'){base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;H[i]=hh;cls[i]=c;continue}
      if(tpl==='opacify'){
        const d=sdPatch(u,v,.08,.92,.1,.9,.05,.035,ox,oy);
        if(d<0){const m=demo[0].map(x=>(x/255)**.85),str=ns(N2,u*.9+ox,v*8+oy);
          r*=m[0];gg*=m[1];b*=m[2];hh=.16*(.5+str)*sstep(0,.01,-d);c=u>.5+.02*(ns(N1,v*3,ox)-.5)?2:1}
        base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;H[i]=hh;cls[i]=c;continue;
      }
      const rag={level:.006,pool:.006,brush:.03,soft:.025,knife:.035,peaks:.04}[p.form]||.025;
      const d=sdPatch(u,v,.08,.92,.1,.9,p.form==='pool'||p.form==='level'?.08:.05,rag,ox,oy);
      if(d<0){
        const inside=-d;
        switch(p.form){
          case'level':hh=h0*sstep(0,.035,inside)*(1+.12*Math.exp(-inside/.03))*(.96+.08*ns(N1,u*3+ox,v*3+oy));break;
          case'pool':hh=h0*Math.sqrt(sstep(0,.07,inside))*(.98+.04*ns(N1,u*2+ox,v*2+oy));break;
          case'brush':{const st=ns(N2,u*.9+ox,v*9+oy),st2=ns(N3,u*.7+oy,v*16+ox),
            dxr=Math.min(u-(.08+.045*ns(N2,ox+.37,v*9+oy)),(.92-.045*ns(N2,ox+.71,v*9+oy+.5))-u),dd=Math.min(inside,dxr);
            hh=h0*(.78+.34*st+.16*(st2-.5))*sstep(0,.03,dd);break}
          case'soft':if(tpl==='fibre'){hh=h0*(.8+.35*ns(N1,u*2.2+ox,v*2.2+oy)+.12*(ns(N2,u*5+oy,v*5+ox)-.5))*sstep(0,.03,inside);break}
            hh=h0*(.72+.55*ns(N1,u*1.6+ox,v*3+oy))*sstep(0,.03,inside)+.1*(1-Math.abs(2*ns(N2,u*1.1+oy,v*4+ox)-1))*sstep(0,.05,inside);break;
          case'knife':case'peaks':{hh=h0*knife(u,v,strokes,ox,oy)*sstep(0,.035,inside);
            if(p.form==='peaks'){const w1=ns(N1,u*.9+ox+.31,v*.9+oy),rg=1-Math.abs(2*ns(N2,u*1.25+w1*.5+oy,v*1.25+ox)-1);
              hh+=h0*.75*Math.pow(rg,3.2)*sstep(0,.06,inside)}break}
        }
        if(p.tooth)P[i]+=p.tooth*.0016*(ns(N3,u*9+ox,v*9+oy)-.5)+p.tooth*.0009*(ns(N3,u*23+oy,v*23+ox)-.5);
        if(tpl==='fibre')P[i]+=.0007*(ns(N3,u*17+ox,v*17+oy)-.5)+.0005*(ns(N3,u*41+oy,v*41+ox)-.5);
        if(tpl==='crackle'){
          let f1=9,f2=9;for(const cc of cells){const dd=Math.hypot(u-cc[0],v-cc[1]);if(dd<f1){f2=f1;f1=dd}else if(dd<f2)f2=dd}
          const e=f2-f1+.004*(ns(N3,u*14+ox,v*14+oy)-.5),cw=.0016+.0035*ns(N1,u*2+ox,v*2+oy),film01=sstep(cw*.4,cw,e);
          P[i]+=.0015*Math.exp(-e/.012)*film01;
          hh=.22*sstep(0,.02,inside)*(.95+.1*ns(N1,u*4,v*4));
          cls[i]=1;H[i]=hh;base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;CR[i]=1-film01;continue;
        }
        c=1;
      }
      base[i*3]=r;base[i*3+1]=gg;base[i*3+2]=b;H[i]=hh;cls[i]=c;
    }}
    /* film fills the canvas weave: damp the weave relief under thicker films */
    for(let y=0,i=0;y<R;y++){const v=(y+.5)*inv;for(let x=0;x<R;x++,i++){if(!cls[i]||H[i]<=0)continue;
      const u=(x+.5)*inv,wv=Math.sin(2*Math.PI*u*W)*Math.sin(2*Math.PI*v*W);P[i]-=.00014*wv*Math.min(.92,H[i]*9)}}
    /* Fibre Paste: dense mat of fine fibres pressed into a soft, matte paste */
    if(tpl==='fibre'){
      const n=Math.round(3200*(R/300)*(R/300));
      for(let k=0;k<n;k++){let x=.09+rnd()*.82,y=.11+rnd()*.78,ang=rnd()*6.28;const len=.012+rnd()*.03,steps=Math.ceil(len*R),hgt=.0006+rnd()*.0008;
        for(let s=0;s<steps;s++){ang+=(rnd()-.5)*.35;x+=Math.cos(ang)/R;y+=Math.sin(ang)/R;const ix=x*R|0,iy=y*R|0;
          if(ix<1||iy<1||ix>=R-1||iy>=R-1)break;const j=iy*R+ix;if(!cls[j])continue;
          P[j]=Math.max(P[j],P[j]*.35+hgt);const sx=Math.round(-Math.sin(ang)),sy=Math.round(Math.cos(ang)),j2=(iy+sy)*R+ix+sx;
          if(cls[j2])P[j2]=Math.max(P[j2],P[j2]*.5+hgt*.4)}}
    }
    if(tpl==='aggregate'){
      const sp=PARTS[p.part]||PARTS.nephC,area=.84*.8,n=Math.round(sp.cov*area/(Math.PI*sp.r*sp.r));
      for(let k=0;k<n;k++){
        const cx=.08+rnd()*.84,cy=.1+rnd()*.8,j0=(cy*R|0)*R+(cx*R|0);if(!cls[j0])continue;
        const r0=sp.r*(.55+rnd()*.9),speck=rnd()<sp.speck,col=speck?[78,76,72]:sp.cols[rnd()*sp.cols.length|0];
        const al=speck?.9:lerp(sp.a[0],sp.a[1],rnd()),verts=sp.shape==='flake'?3+(rnd()*3|0):5+(rnd()*3|0),rot=rnd()*6.28;
        const el=sp.shape==='flake'?1.3+rnd()*1.2:1;
        const tx=(rnd()-.5)*.5,ty=(rnd()-.5)*.5,lift=r0*sp.hgt*(.4+rnd()*.8); /* flake plane tilt and height */
        const jit=[],ang=[];for(let q=0;q<verts;q++){jit.push(sp.shape==='flake'?.55+rnd()*.6:.75+rnd()*.35);ang.push((q+.2+rnd()*.6)/verts*Math.PI*2)}
        const bb=Math.ceil(r0*el*R*1.15)+1,px=cx*R,py=cy*R,ca=Math.cos(rot),sa=Math.sin(rot);
        for(let yy=Math.max(0,py-bb|0);yy<Math.min(R,py+bb+1|0);yy++)for(let xx=Math.max(0,px-bb|0);xx<Math.min(R,px+bb+1|0);xx++){
          const dx=(xx+.5-px)/R,dy=(yy+.5-py)/R;let ax=dx*ca+dy*sa,ay=-dx*sa+dy*ca;ax/=el;
          const th=Math.atan2(ay,ax),dist=Math.hypot(ax,ay);
          let rr;if(sp.shape==='round')rr=r0*(.9+.12*Math.sin(th*3+rot));
          else if(sp.shape==='flake'){ /* straight-edged polygon: distance to the edge between the two bracketing vertices */
            const ta=th<0?th+Math.PI*2:th;let q=0;while(q<verts-1&&ta>ang[q+1])q++;
            let i0=q,i1=(q+1)%verts;if(ta<ang[0]){i0=verts-1;i1=0}
            const x0=Math.cos(ang[i0])*jit[i0],y0=Math.sin(ang[i0])*jit[i0],x1=Math.cos(ang[i1])*jit[i1],y1=Math.sin(ang[i1])*jit[i1];
            const ex=x1-x0,ey=y1-y0,cx2=Math.cos(ta),cy2=Math.sin(ta),den=cx2*ey-cy2*ex;
            rr=r0*(Math.abs(den)>1e-6?(x0*ey-y0*ex)/den:jit[i0])}
          else{const seg=(th+Math.PI)/(2*Math.PI)*verts,i0=Math.floor(seg)%verts,i1=(i0+1)%verts,f=seg-Math.floor(seg);rr=r0*lerp(jit[i0],jit[i1],f)}
          if(dist>rr)continue;const t=dist/rr,j=yy*R+xx;if(!cls[j])continue;
          let ph,a2=al,cc=col;
          if(sp.shape==='flake'){ph=Math.max(.0002,lift+tx*dx+ty*dy);DM[j]=70;
            if(t>.9){a2=.22;cc=[250,252,255]}}          /* bright refracting edge */
          else ph=sp.shape==='round'?r0*sp.hgt*Math.sqrt(1-t*t):r0*sp.hgt*(1-t);
          if(sp.shape!=='round'&&sp.shape!=='flake'&&ph*1.0001<P[j])continue;
          if(sp.shape==='flake'&&ph<P[j]*.98){a2*=.5}
          const shd=sp.shape==='round'?.85+.3*(1-t):1;
          base[j*3]=lerp(base[j*3],cc[0]*shd,a2);base[j*3+1]=lerp(base[j*3+1],cc[1]*shd,a2);base[j*3+2]=lerp(base[j*3+2],cc[2]*shd,a2);
          P[j]=Math.max(P[j],ph);
        }
      }
      for(let j=0;j<N;j++)if(cls[j]){const ph=P[j]/HS;H[j]=Math.max(H[j]*.3,H[j]-ph*.7)}
    }
    /* contact shadow */
    const ao=new Uint8ClampedArray(N);{
      const T=new Float32Array(N),B=new Float32Array(N),C=new Float32Array(N),rad=Math.max(2,Math.round(R*.013));
      for(let i=0;i<N;i++)T[i]=H[i]*HS+P[i];
      const blur=(src,dst)=>{
        for(let y=0;y<R;y++){let acc=0;for(let x=-rad;x<=rad;x++)acc+=src[y*R+clamp(x,0,R-1)];
          for(let x=0;x<R;x++){B[y*R+x]=acc/(2*rad+1);acc+=src[y*R+Math.min(R-1,x+rad+1)]-src[y*R+Math.max(0,x-rad)]}}
        for(let x=0;x<R;x++){let acc=0;for(let y=-rad;y<=rad;y++)acc+=B[clamp(y,0,R-1)*R+x];
          for(let y=0;y<R;y++){dst[y*R+x]=acc/(2*rad+1);acc+=B[Math.min(R-1,y+rad+1)*R+x]-B[Math.max(0,y-rad)*R+x]}}};
      blur(T,C);const C2=new Float32Array(N);blur(C,C2);C.set(C2);
      const off=Math.max(1,Math.round(R*.012)),A=new Float32Array(N);
      for(let y=0;y<R;y++)for(let x=0;x<R;x++){const i=y*R+x,j=Math.max(0,y-off)*R+Math.max(0,x-off);
        A[i]=clamp(Math.max((C[i]-T[i])*520,(C[j]-T[i])*420)*127.5,0,46)}
      const r2=Math.max(1,Math.round(R*.004)),A2=new Float32Array(N);
      for(let y=0;y<R;y++)for(let x=0;x<R;x++){let acc=0,n=0;for(let k=-r2;k<=r2;k++){const xx=x+k;if(xx<0||xx>=R)continue;acc+=A[y*R+xx];n++}A2[y*R+x]=acc/n}
      for(let y=0;y<R;y++)for(let x=0;x<R;x++){let acc=0,n=0;for(let k=-r2;k<=r2;k++){const yy=y+k;if(yy<0||yy>=R)continue;acc+=A2[yy*R+x];n++}ao[y*R+x]=acc/n}}
    const gxH=new Float32Array(N),gyH=new Float32Array(N),gxP=new Float32Array(N),gyP=new Float32Array(N),sc=R/2;
    for(let y=0;y<R;y++)for(let x=0;x<R;x++){const i=y*R+x,l=x?i-1:i,rr=x<R-1?i+1:i,up=y?i-R:i,dn=y<R-1?i+R:i;
      gxH[i]=(H[rr]-H[l])*sc*HS;gyH[i]=(H[dn]-H[up])*sc*HS;gxP[i]=(P[rr]-P[l])*sc;gyP[i]=(P[dn]-P[up])*sc}
    return{R,base,H,cls,gxH,gyH,gxP,gyP,classes,crack:tpl==='crackle'?CR.slice(0,N):null,ao,dm:tpl==='aggregate'?DM.slice(0,N):null};
  }

  /* fixed rim light from upper right adds glints on glossy relief */
  const L2=(()=>{const x=.55,y=-.5,z=.67,l=Math.hypot(x,y,z);return[x/l,y/l,z/l]})();
  const H2=(()=>{const x=L2[0],y=L2[1],z=L2[2]+1,l=Math.hypot(x,y,z);return[x/l,y/l,z/l]})();
  function shade(b,t,Lx,Ly,uvAmt,img){
    const R=b.R,d=img.data,inv=1/R,Lz=.85,uvA=clamp(uvAmt||0,0,1);
    const tk=sstep(.25,1,t),tg=sstep(.5,1,t),th=sstep(0,1,t);
    const cl=b.classes.map(c=>{const sp=specFor(lerp(c.gw,c.g,tg)),lut=new Float32Array(1025);
      for(let i=0;i<=1024;i++){const x=i/1024;lut[i]=sp.amp*Math.pow(x,sp.sh)+sp.halo*Math.pow(x,30)}
      return{k:lerp(c.kw,c.k,tk),hs:lerp(c.shrink,1,th),lut,col:c.colW.map((w,j)=>lerp(w,c.col[j],tk)),uv:c.uv}});
    const crackT=b.crack?sstep(.55,1,t):0;
    for(let y=0,i=0;y<R;y++){const v=(y+.5)*inv;for(let x=0;x<R;x++,i++){const u=(x+.5)*inv;
      const ci=b.cls[i],c=cl[ci];let hh=b.H[i]*c.hs;
      if(b.crack&&b.crack[i]>0)hh*=1-b.crack[i]*crackT;
      const a=ci?1-Math.exp(-c.k*hh):0;
      let r=b.base[i*3]*(1-a)+c.col[0]*a,g=b.base[i*3+1]*(1-a)+c.col[1]*a,bl=b.base[i*3+2]*(1-a)+c.col[2]*a;
      let gx=b.gxH[i]*c.hs+b.gxP[i],gy=b.gyH[i]*c.hs+b.gyP[i];
      if(b.crack&&crackT<1&&ci){gx=b.gxH[i]*c.hs+b.gxP[i]*crackT;gy=b.gyH[i]*c.hs+b.gyP[i]*crackT}
      const s2=gx*gx+gy*gy,nl=1/Math.sqrt(s2+1),nx=-gx*nl,ny=-gy*nl,nz=nl;
      let lx=Lx-u,ly=Ly-v,lz=Lz;const ll=1/Math.sqrt(lx*lx+ly*ly+lz*lz);lx*=ll;ly*=ll;lz*=ll;
      const edgeT=ci?sstep(0,.025,hh):0,dm=b.dm?b.dm[i]/255:1,dif=(nx*lx+ny*ly+nz*lz)/lz,wd=(ci?lerp(1,.18+.82*a,edgeT):1)*dm;
      let s=1+wd*(clamp(dif,.5,1.7)-1)*.85;
      if(ci&&c.k<3)s*=1-(1-a)*Math.min(.12,s2*.22)*sstep(0,.04,hh);
      s*=1-(b.ao[i]/255)*(ci?lerp(1,.2+.8*a,edgeT):1)*dm;
      if(b.crack&&b.crack[i]>0)s*=1-.4*b.crack[i]*crackT;
      const hx=lx,hy=ly,hz=lz+1,hl=1/Math.sqrt(hx*hx+hy*hy+hz*hz),ndh=(nx*hx+ny*hy+nz*hz)*hl;
      const fr=1+1.3*(1-nz),ndh2=nx*H2[0]+ny*H2[1]+nz*H2[2];
      const sp=(ndh>0?c.lut[(ndh*1024)|0]:0)*fr+(ndh2>0?c.lut[(ndh2*1024)|0]*.3*(1-nz)*3:0);
      r=r*s+255*sp;g=g*s+255*sp;bl=bl*s+255*sp;
      if(uvA>0){
        let ur=r*.2+6,ug=g*.1+4,ub=bl*.42+16;
        if(c.uv&&ci){const ga=1-Math.exp(-9*hh);ur+=40*ga;ug+=150*ga;ub+=200*ga}
        r=lerp(r,ur,uvA);g=lerp(g,ug,uvA);bl=lerp(bl,ub,uvA);
      }
      const o=i*4;d[o]=r;d[o+1]=g;d[o+2]=bl;d[o+3]=255}}
  }
  return{build,shade,parseGF};
}


  var REST = [0.36, 0.44];
  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var state = { t: 1 };
  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function ease(k) { return k * k * (3 - 2 * k); }
  function parseGF(s) {
    if (!s) return null;
    var m = String(s).match(/[\d.]+/g);
    if (!m) return null;
    var a = parseFloat(m[0]), b = m[1] ? parseFloat(m[1]) : a;
    return [a, b];
  }

  /* ---------- renderer: worker first, main thread as fallback ---------- */
  function workerMain() {
    var C = MS_CORE(), items = new Map(), cache = new Map(), pending = new Map(), busy = false, MAX = 18;
    function buf(it) {
      var key = it.id + '@' + it.R, b = cache.get(key);
      if (b) { cache.delete(key); cache.set(key, b); return b; }
      b = C.build(it.p, it.R); cache.set(key, b);
      if (cache.size > MAX) cache.delete(cache.keys().next().value);
      return b;
    }
    function loop() {
      var n = pending.entries().next();
      if (n.done) { busy = false; return; }
      var id = n.value[0], m = n.value[1]; pending.delete(id);
      var it = items.get(id);
      if (it) {
        if (it.R !== m.R) { it.R = m.R; it.cv.width = m.R; it.cv.height = m.R; it.img = null; }
        if (!it.img) it.img = it.ctx.createImageData(it.R, it.R);
        C.shade(buf(it), m.t, m.Lx, m.Ly, m.uv, it.img);
        it.ctx.putImageData(it.img, 0, 0);
        if (!it.done) { it.done = true; self.postMessage({ type: 'ready', id: id }); }
      }
      setTimeout(loop, 0);
    }
    self.onmessage = function (e) {
      var m = e.data;
      if (m.type === 'ping') self.postMessage({ type: 'pong' });
      else if (m.type === 'init') items.set(m.id, { id: m.id, cv: m.cv, ctx: m.cv.getContext('2d'), p: m.p, R: 0 });
      else if (m.type === 'paint') { pending.set(m.id, m); if (!busy) { busy = true; setTimeout(loop, 0); } }
      else if (m.type === 'drop') { items.delete(m.id); pending.delete(m.id); }
    };
  }

  function makeLocal(onReady) {
    var C = null, items = new Map(), cache = new Map(), pending = new Map(), busy = false, MAX = 10;
    var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1); };
    function buf(it) {
      var key = it.id + '@' + it.R, b = cache.get(key);
      if (b) { cache.delete(key); cache.set(key, b); return b; }
      b = C.build(it.p, it.R); cache.set(key, b);
      if (cache.size > MAX) cache.delete(cache.keys().next().value);
      return b;
    }
    function loop() {
      if (!C) C = MS_CORE();
      var n = pending.entries().next();
      if (n.done) { busy = false; return; }
      var id = n.value[0], m = n.value[1]; pending.delete(id);
      var it = items.get(id);
      if (it) {
        if (it.R !== m.R) { it.R = m.R; it.cv.width = m.R; it.cv.height = m.R; it.img = null; }
        if (!it.img) it.img = it.ctx.createImageData(it.R, it.R);
        C.shade(buf(it), m.t, m.Lx, m.Ly, m.uv, it.img);
        it.ctx.putImageData(it.img, 0, 0);
        if (!it.done) { it.done = true; onReady(id); }
      }
      idle(loop);
    }
    return {
      init: function (id, cv, p) { items.set(id, { id: id, cv: cv, ctx: cv.getContext('2d'), p: p, R: 0 }); },
      paint: function (m) { pending.set(m.id, m); if (!busy) { busy = true; idle(loop); } },
      drop: function (id) { items.delete(id); pending.delete(id); }
    };
  }

  var renderer = null, waiting = [];
  function onReady(id) { var c = all.get(id); if (c) c.root.classList.add('is-ready'); }
  function useLocal() { renderer = makeLocal(onReady); waiting.forEach(function (f) { f(); }); waiting = []; }
  (function startRenderer() {
    var ok = false;
    try { ok = !!(window.Worker && window.OffscreenCanvas && HTMLCanvasElement.prototype.transferControlToOffscreen); } catch (e) {}
    if (!ok) return useLocal();
    var w, url, settled = false;
    try {
      url = URL.createObjectURL(new Blob([MS_CORE.toString() + '\n(' + workerMain.toString() + ')();'], { type: 'text/javascript' }));
      w = new Worker(url);
    } catch (e) { return useLocal(); }
    var timer = setTimeout(function () { if (!settled) { settled = true; try { w.terminate(); } catch (e) {} useLocal(); } }, 3000);
    w.onerror = function () { if (!settled) { settled = true; clearTimeout(timer); useLocal(); } };
    w.onmessage = function (e) {
      if (e.data.type === 'pong' && !settled) {
        settled = true; clearTimeout(timer);
        renderer = {
          init: function (id, cv, p) { var off = cv.transferControlToOffscreen(); w.postMessage({ type: 'init', id: id, cv: off, p: p }, [off]); },
          paint: function (m) { w.postMessage(m); },
          drop: function (id) { w.postMessage({ type: 'drop', id: id }); }
        };
        waiting.forEach(function (f) { f(); }); waiting = [];
      } else if (e.data.type === 'ready') onReady(e.data.id);
    };
    w.postMessage({ type: 'ping' });
  })();
  function whenReady(f) { if (renderer) f(); else waiting.push(f); }

  /* ---------- shared item plumbing ---------- */
  var all = new Map(), nextId = 1;
  function readData(root) {
    var s = root.querySelector('script[data-ms-data]');
    if (!s) return null;
    var d; try { d = JSON.parse(s.textContent); } catch (e) { return null; }
    if (d && typeof d.r === 'string') { try { d.r = JSON.parse(d.r); } catch (e) { d.r = null; } }  /* text-type metafield */
    if (!d || !d.r || !d.r.tpl) return null;
    var p = {}, k; for (k in d.r) p[k] = d.r[k];
    p.h = d.h || 'medium'; p.gf = parseGF(d.gf);
    return p;
  }
  function paintItem(c) {
    if (!renderer) return;
    if (!c.inited) { renderer.init(c.id, c.cv, c.p); c.inited = true; }
    var w = c.art.clientWidth || 240;
    var R = clamp(Math.round(w * Math.min(window.devicePixelRatio || 1, c.dprCap)), c.minR, c.maxR);
    renderer.paint({ type: 'paint', id: c.id, R: R, t: c.t(), Lx: c.L[0], Ly: c.L[1], uv: c.glow });
    c.paintedT = c.t();
  }
  var rafQ = new Set(), rafOn = false;
  function queuePaint(c) {
    rafQ.add(c);
    if (!rafOn) { rafOn = true; requestAnimationFrame(function () { rafOn = false; var q = Array.from(rafQ); rafQ.clear(); q.forEach(paintItem); }); }
  }
  function glowTo(c, target, dur) {
    cancelAnimationFrame(c.gRaf);
    var from = c.glow, t0 = performance.now(); dur = reduceMQ.matches ? 1 : (dur || 420);
    function step(now) {
      var k = Math.min(1, (now - t0) / dur);
      c.glow = from + (target - from) * ease(k); paintItem(c);
      if (k < 1) c.gRaf = requestAnimationFrame(step);
    }
    c.gRaf = requestAnimationFrame(step);
  }

  /* ---------- collection cards ---------- */
  var cardIO = new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      var c = e.target._ms; if (!c) return;
      c.visible = e.isIntersecting;
      if (c.visible && c.paintedT !== c.t()) whenReady(function () { paintItem(c); });
    });
  }, { rootMargin: '300px 0px' });
  var uvIO = new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (!e.isIntersecting) return;
      var c = e.target._ms; uvIO.unobserve(e.target);
      setTimeout(function () { glowTo(c, 1, 500); setTimeout(function () { glowTo(c, 0, 700); }, 1400); }, 500);
    });
  }, { threshold: 0.6 });

  function initCard(root) {
    if (root._ms) return;
    var p = readData(root), cv = root.querySelector('canvas');
    if (!p || !cv) return;
    var stat = root.hasAttribute('data-ms-static');
    var c = { id: nextId++, root: root, art: root, cv: cv, p: p, L: REST.slice(), glow: 0, minR: stat ? 72 : 160, maxR: stat ? 160 : 380, dprCap: stat ? 2 : 1.5,
      card: stat ? false : undefined, t: function () { return stat ? 1 : state.t; }, paintedT: -1, visible: false };
    root._ms = c; all.set(c.id, c); cardIO.observe(root);
    if (stat) return;
    var hoverEl = root.closest('.product-item, .product-card, li, article') || root;
    hoverEl.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' || !hoverMQ.matches) return;
      var r = root.getBoundingClientRect(), u = (e.clientX - r.left) / r.width, v = (e.clientY - r.top) / r.height;
      if (!reduceMQ.matches) {
        root.style.setProperty('--ms-ry', ((u - 0.5) * 12).toFixed(2) + 'deg');
        root.style.setProperty('--ms-rx', (-(v - 0.5) * 12).toFixed(2) + 'deg');
        root.style.setProperty('--ms-tx', (u - 0.5).toFixed(3));
        root.style.setProperty('--ms-ty', (v - 0.5).toFixed(3));
      }
      c.L = [clamp(u, -0.3, 1.3), clamp(v, -0.3, 1.3)]; queuePaint(c);
    });
    hoverEl.addEventListener('pointerenter', function (e) { if (p.uv && e.pointerType === 'mouse' && hoverMQ.matches) glowTo(c, 1); });
    hoverEl.addEventListener('pointerleave', function () {
      ['--ms-ry', '--ms-rx', '--ms-tx', '--ms-ty'].forEach(function (k) { root.style.removeProperty(k); });
      c.L = REST.slice(); queuePaint(c);
      if (p.uv) glowTo(c, 0);
    });
    if (p.uv && !hoverMQ.matches) uvIO.observe(root);
  }

  /* Dry / Wet toggle animates the drying of every card on screen */
  var dryRaf = 0;
  function setState(target) {
    cancelAnimationFrame(dryRaf);
    document.querySelectorAll('[data-ms-toggle] [data-ms-state]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(Number(b.getAttribute('data-ms-state')) === target));
    });
    var from = state.t, t0 = performance.now(), dur = reduceMQ.matches ? 1 : 1100, last = 0;
    function step(now) {
      var k = Math.min(1, (now - t0) / dur);
      if (now - last > 70 || k === 1) {
        last = now; state.t = from + (target - from) * ease(k);
        all.forEach(function (c) { if (c.card !== false && c.visible) paintItem(c); });
      }
      if (k < 1) dryRaf = requestAnimationFrame(step); else state.t = target;
    }
    dryRaf = requestAnimationFrame(step);
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-ms-toggle] [data-ms-state]');
    if (b) setState(Number(b.getAttribute('data-ms-state')));
  });

  /* ---------- size cutouts on the product page's live swatch slide ----------
     <script data-ta-cutouts data-slide="ta-medium|ta-swatch" data-target="img selector">
       {"s": selected variant id, "d": default image (optional), "a": [variant ids], "v": {"variant id": {"u": cutout url, "m": media id}}}
     Choosing a size whose variant image is a jar or bottle cutout swaps the image floating on the swatch
     (colour cards: the tube and its shadow); other sizes bring back the default. Focal would scroll the
     gallery to that variant's image, which is not a slide here, so the event is pointed at the live
     swatch slide instead and the swatch stays in view. */
  function initSizeCutouts(holder) {
    if (holder._ta) return;
    holder._ta = 1;
    var map;
    try { map = JSON.parse(holder.textContent); } catch (e) { return; }
    var slideId = holder.getAttribute('data-slide') || 'ta-medium';
    var root = holder.closest('.product__media-item') || holder.parentNode;
    var imgs = Array.prototype.slice.call(root.querySelectorAll(holder.getAttribute('data-target') || '.ms-card__cutout'));
    if (!imgs.length) return;
    var orig = imgs.map(function (im) { return { src: im.getAttribute('src'), srcset: im.getAttribute('srcset') }; });
    var mine = {}, v = map.v || {};
    (map.a || []).forEach(function (id) { mine[String(id)] = 1; });
    var pm = holder.closest('product-media');
    var form = pm && pm.getAttribute('form-id') ? document.getElementById(pm.getAttribute('form-id')) : null;
    function urlFor(id) { var e = v[String(id)]; return e && e.u ? e.u : (map.d || null); }
    function apply(url) {
      imgs.forEach(function (im, i) {
        if (url) {
          im.removeAttribute('srcset'); im.setAttribute('src', url);
          im.classList.toggle('is-cut', !map.d);
        } else {
          if (orig[i].srcset) im.setAttribute('srcset', orig[i].srcset); else im.removeAttribute('srcset');
          im.setAttribute('src', orig[i].src); im.classList.remove('is-cut');
        }
      });
    }
    var shown = urlFor(map.s);
    if (shown && shown !== orig[0].src) apply(shown);
    function show(id) {
      var url = urlFor(id);
      if (url === shown) return;
      shown = url;
      imgs.forEach(function (im) { im.classList.add('is-swapping'); });
      var pre = new Image();
      pre.onload = pre.onerror = function () {
        if (shown !== url) return;
        apply(url);
        requestAnimationFrame(function () { requestAnimationFrame(function () {
          imgs.forEach(function (im) { im.classList.remove('is-swapping'); });
        }); });
      };
      pre.src = url || orig[0].src;
    }
    var car = pm && pm.querySelector('flickity-carousel');
    function toSlide() {
      if (!car || typeof car.select !== 'function') return;
      try { Promise.resolve(car.select('[data-media-id="' + slideId + '"]')).catch(function () {}); } catch (err) {}
    }
    /* Runs before the gallery's own listener (capture phase). The variant's image is pointed at the
       live swatch slide, so the gallery never jumps to a size photo; the slide is also brought into
       view when it isn't (the gallery skips a select it thinks it already made). */
    document.addEventListener('variant:changed', function (e) {
      var d = e.detail, variant = d && d.variant;
      if (!holder.isConnected || !variant || !mine[String(variant.id)]) return;
      if (form && e.target !== form) return;
      show(variant.id);
      var fm = variant.featured_media;
      if (fm) {
        try { d.variant = Object.assign({}, variant, { featured_media: Object.assign({}, fm, { id: slideId }) }); } catch (err) {}
      }
      setTimeout(toSlide, 0);
    }, true);
    /* fallback for themes that only update the form's variant id */
    document.addEventListener('change', function () {
      if (!holder.isConnected || !form) return;
      setTimeout(function () {
        var f = form.elements && form.elements.namedItem('id');
        var id = f && (f.value || (f[0] && f[0].value));
        if (id && mine[String(id)]) show(id);
      }, 40);
    });
  }

  /* ---------- product page: section ([data-ms-specs]) and gallery slide ([data-ms-specs][data-ms-gallery]) ---------- */
  function initSpecs(sec) {
    if (sec._ms) return;
    var p = readData(sec), art = sec.querySelector('[data-ms-art]'), cv = art && art.querySelector('canvas');
    if (!p || !cv) return;
    var gallery = sec.hasAttribute('data-ms-gallery');
    var range = sec.querySelector('[data-ms-range]'), play = sec.querySelector('[data-ms-play]'), hint = sec.querySelector('[data-ms-hint]');
    var setBtns = Array.prototype.slice.call(sec.querySelectorAll('[data-ms-set]'));
    var c = { id: nextId++, root: sec, art: art, cv: cv, p: p, L: REST.slice(), glow: 0, minR: 240, maxR: 760, dprCap: 2, card: false,
      tv: 1, t: function () { return c.tv; }, paintedT: -1, visible: true };
    sec._ms = c; all.set(c.id, c);
    var exp = Number(sec.getAttribute('data-ms-export')) || 0;
    if (exp) {  /* static export (templates/product.export.liquid): fixed size, dry, rest light, no interaction */
      c.minR = c.maxR = exp;
      whenReady(function () { paintItem(c); });
      return;
    }
    if (hint) {
      if (gallery && !p.uv) hint.hidden = true;
      else {
        var key = p.uv ? (hoverMQ.matches ? 'hintUv' : 'hintUvTouch') : 'hintLight';
        if (hint.dataset[key]) hint.textContent = hint.dataset[key];
      }
    }
    whenReady(function () { paintItem(c); });
    var playRaf = 0;
    function pressed(target) {
      setBtns.forEach(function (b) { b.setAttribute('aria-pressed', String(Number(b.getAttribute('data-ms-set')) === target)); });
    }
    function animateTo(target, dur, from, linear) {
      cancelAnimationFrame(playRaf);
      var f = from == null ? c.tv : from, t0 = performance.now();
      dur = reduceMQ.matches ? 1 : dur;
      pressed(target);
      function step(now) {
        var k = Math.min(1, (now - t0) / dur);
        c.tv = f + (target - f) * (linear ? k : ease(k));
        if (range) range.value = Math.round(c.tv * 1000);
        paintItem(c);
        if (k < 1) playRaf = requestAnimationFrame(step);
      }
      playRaf = requestAnimationFrame(step);
    }
    if (range) range.addEventListener('input', function () {
      cancelAnimationFrame(playRaf); c.tv = Number(range.value) / 1000; pressed(Math.round(c.tv)); queuePaint(c);
    });
    if (play) play.addEventListener('click', function () { animateTo(1, 3600, 0, true); });
    setBtns.forEach(function (b) { b.addEventListener('click', function () { animateTo(Number(b.getAttribute('data-ms-set')), 1100); }); });
    /* keep presses on the controls from starting a carousel drag */
    Array.prototype.forEach.call(sec.querySelectorAll('[data-ms-controls]'), function (el) {
      ['pointerdown', 'mousedown', 'touchstart'].forEach(function (ev) { el.addEventListener(ev, function (e) { e.stopPropagation(); }, { passive: true }); });
    });
    art.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' && (gallery || !e.buttons)) return;
      var r = art.getBoundingClientRect(), u = (e.clientX - r.left) / r.width, v = (e.clientY - r.top) / r.height;
      c.L = [clamp(u, -0.3, 1.3), clamp(v, -0.3, 1.3)];
      if (!reduceMQ.matches) { art.style.setProperty('--ms-tx', (u - 0.5).toFixed(3)); art.style.setProperty('--ms-ty', (v - 0.5).toFixed(3)); }
      queuePaint(c);
    });
    art.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      c.L = REST.slice(); art.style.removeProperty('--ms-tx'); art.style.removeProperty('--ms-ty'); queuePaint(c);
    });
    if (!gallery) art.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') art.setPointerCapture(e.pointerId); });
    if (p.uv) {
      art.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') glowTo(c, 1); });
      art.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') glowTo(c, 0); });
      art.addEventListener('click', function (e) {
        if (hoverMQ.matches || (e.target.closest && e.target.closest('[data-ms-controls]'))) return;
        glowTo(c, c.glow > 0.5 ? 0 : 1);
      });
    }
  }

  /* ---------- boot, plus cards added later by filtering, infinite scroll or quick view ---------- */
  function scan(root) {
    (root.querySelectorAll ? root : document).querySelectorAll('[data-ms-card]').forEach(initCard);
    (root.querySelectorAll ? root : document).querySelectorAll('[data-ms-specs]').forEach(initSpecs);
    (root.querySelectorAll ? root : document).querySelectorAll('[data-ta-cutouts]').forEach(initSizeCutouts);
    if (root.matches && root.matches('[data-ms-card]')) initCard(root);
    if (root.matches && root.matches('[data-ms-specs]')) initSpecs(root);
    if (root.matches && root.matches('[data-ta-cutouts]')) initSizeCutouts(root);
  }
  function prune() {
    all.forEach(function (c, id) {
      if (!c.root.isConnected) { all.delete(id); cardIO.unobserve(c.root); uvIO.unobserve(c.root); if (renderer && c.inited) renderer.drop(id); }
    });
  }
  new MutationObserver(function (ms) {
    var added = false;
    ms.forEach(function (m) { m.addedNodes.forEach(function (n) { if (n.nodeType === 1) { scan(n); added = true; } }); });
    if (ms.some(function (m) { return m.removedNodes.length; })) prune();
  }).observe(document.documentElement, { childList: true, subtree: true });
  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () { all.forEach(function (c) { if (c.visible) paintItem(c); }); }, 250);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(document); });
  else scan(document);

  window.TriArtMediums = { refresh: function () { scan(document); }, setState: setState };
})();
